import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router'
import Icon from '@/components/icon'
import NoChatBG from '@/features/Chat/images/no-chat-placeholder.png'
import useMediaQuery from '@/hooks/use-media-query'
import { BulkContext } from '@/pages/Chat/context'
import { colors, breakpoints, fontSizes, fontWeights } from '@/styles'
import { showDeleteConfirmationModal } from '@/features/Chat/chat-side-bar/delete-modal'
import { showMarkAsReadConfirmationModal } from '@/features/Chat/chat-side-bar/mark-as-read-modal'
import { DeleteFillIcon, MarkAsRead, CloseFillIcon } from '@/components/icons'

const EmptyWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  background-color: ${colors.lightGrey};
  padding-bottom: 58px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  width: 100%;
`

const BackIcon = styled(Icon)`
  transform: rotate(-90deg);
  cursor: pointer;
  position: absolute;
  left: 10px;
  top: 30px;
  display: none;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

const HeaderWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`

const Text = styled.p`
  font-size: 18px;
  font-weight: ${fontWeights.medium};
  line-height: 30px;
  margin-top: 30px;
  color: ${colors.regular};
  text-align: center;

  a {
    color: ${colors.secondary};
    text-decoration: underline;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: ${fontSizes.regular};
    line-height: 24px;
  }
`

const NoChatImage = styled.img`
  max-width: 250px;
`

const BulkActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  border-left: 1px solid ${colors.grey80};
  padding-left: 25px;
`

const BulkActionMenus = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  p {
    margin: 12px 0;
    padding-left: 10px;
    font-size: 14px;
    line-height: 16px;
  }
`

const MarkAsReadText = styled(Text)`
  color: ${colors.link};
`

const DeleteText = styled(Text)`
  color: ${colors.link};
`

const CancelText = styled(Text)`
  color: ${colors.red56};
`

const EmptyChatRoom = ({
  handleBack,
  message,
  sideBarHeader,
  chatFooterButton,
  noTimelines,
  ...rest
}) => {
  const { t } = useTranslation()
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const HeaderComponent = sideBarHeader
  const ButtonComponent = chatFooterButton

  const bulkContext = useContext(BulkContext)

  const { selectMode, setSelectMode, selectedChat, setIsCheckAll, setBulkResult } = bulkContext

  const history = useHistory()

  const selectionEmpty = selectedChat.length === 0

  return (
    <EmptyWrapper {...rest}>
      {sideBarHeader && noTimelines && displayTablet ? (
        <HeaderWrapper>
          <HeaderComponent />
        </HeaderWrapper>
      ) : (
        <BackIcon id="arrow" height={14} width={18} onClick={handleBack} />
      )}
      <NoChatImage src={NoChatBG} alt="" />
      <Text>
        {selectMode
          ? `${selectedChat.length}/50 chats selected`
          : message || t('b.chat.view.no_chat_selected.text', 'No chat selected')}
      </Text>
      {selectMode && (
        <BulkActionWrapper>
          <BulkActionMenus>
            <MarkAsRead fill={colors.link} />
            <MarkAsReadText
              onClick={() => {
                if (selectionEmpty) return
                showMarkAsReadConfirmationModal({
                  selectedChat,
                  setBulkResult,
                  setIsCheckAll,
                  setSelectMode
                })
              }}
            >
              {t('b.chat.view.bulk_edit.menu.mark_as_read', 'Mark as read')}
            </MarkAsReadText>
          </BulkActionMenus>
          <BulkActionMenus>
            <DeleteFillIcon fill={colors.link} />
            <DeleteText
              onClick={() => {
                if (selectionEmpty) return
                showDeleteConfirmationModal({
                  selectedChat,
                  setBulkResult,
                  history,
                  setSelectMode,
                  setIsCheckAll
                })
              }}
            >
              {t('common.delete', 'Delete')}
            </DeleteText>
          </BulkActionMenus>
          <BulkActionMenus>
            <CloseFillIcon fill={colors.red56} />
            <CancelText
              onClick={() => {
                setSelectMode(false)
                setIsCheckAll(false)
              }}
            >
              {t('common.cancel', 'Cancel')}
            </CancelText>
          </BulkActionMenus>
        </BulkActionWrapper>
      )}
      {chatFooterButton && noTimelines && displayTablet && (
        <ButtonWrapper>
          <ButtonComponent />
        </ButtonWrapper>
      )}
    </EmptyWrapper>
  )
}

export default EmptyChatRoom
